import { useState } from 'react';

/**
 * Shared filter controls, matching the mobile app's control language.
 *
 * The app uses chips, steppers and uppercase section labels instead of stacked
 * Bootstrap form rows, which is what lets a whole filter fit in one scroll with
 * nothing hidden behind tabs. The pattern transfers; the palette does not - the
 * app is dark, the sidebar is light, so colours come from the web tokens.
 *
 * Every control here is LIVE: changing a value calls onChange immediately and
 * the map updates. There is deliberately no Apply button - the web queries are
 * cheap enough that batching would only make the UI feel slower than it is.
 */

const t = {
  group: { borderTop: '1px solid var(--gf-border-color, #E5E7EB)' },
  groupHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
    padding: '11px 0 10px', background: 'none', border: 0, cursor: 'pointer', textAlign: 'left',
    fontFamily: 'inherit',
  },
  groupRight: { display: 'flex', alignItems: 'center', gap: 7 },
  chevron: {
    fontSize: 13, lineHeight: 1, color: 'var(--gf-secondary-400, #9AA3AB)',
    transition: 'transform 0.15s ease', display: 'inline-block',
  },
  groupBody: { paddingBottom: 6 },
  section: {
    borderTop: '1px solid var(--gf-border-color, #E5E7EB)',
    padding: 'var(--gf-space-3, 12px) 0 2px',
  },
  sectionFirst: { borderTop: 0, paddingTop: 2 },
  head: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 },
  label: {
    fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
    color: 'var(--gf-secondary-500, #4B5563)',
  },
  meta: { fontSize: '11.5px', color: 'var(--gf-secondary-400, #9AA3AB)' },
  action: {
    fontSize: '11.5px', fontWeight: 600, color: 'var(--gf-primary-500, #D2530E)',
    background: 'none', border: 0, padding: 0, cursor: 'pointer',
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 9 },
  chip: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    height: 30, padding: '0 11px', borderRadius: 9, cursor: 'pointer',
    background: 'var(--gf-surface, #FFFFFF)',
    border: '1px solid var(--gf-border-color, #E5E7EB)',
    color: 'var(--gf-secondary-500, #4B5563)', fontSize: '12.5px',
    fontFamily: 'inherit', lineHeight: 1,
  },
  chipOn: {
    background: 'var(--gf-primary-50, #FFF4ED)',
    border: '1.5px solid var(--gf-primary-400, #FD7218)',
    color: 'var(--gf-primary-500, #D2530E)', fontWeight: 600,
  },
  mono: { fontFamily: '"IBM Plex Mono", ui-monospace, monospace', fontVariantNumeric: 'tabular-nums' },
  stepbox: {
    display: 'flex', alignItems: 'center', gap: 6, height: 38, padding: '0 5px 0 11px',
    background: 'var(--gf-surface, #FFFFFF)',
    border: '1px solid var(--gf-border-color, #E5E7EB)',
    borderRadius: 10, marginTop: 9,
  },
  stepInput: {
    flexGrow: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent',
    fontFamily: '"IBM Plex Mono", ui-monospace, monospace', fontVariantNumeric: 'tabular-nums',
    fontSize: '14px', color: 'var(--gf-secondary-800, #12181B)',
  },
  unit: { color: 'var(--gf-secondary-400, #9AA3AB)', fontSize: '11px', marginRight: 4 },
  pm: {
    width: 28, height: 28, borderRadius: 7, flex: 'none', cursor: 'pointer',
    background: 'var(--gf-secondary-50, #F5F6F7)',
    border: '1px solid var(--gf-border-color, #E5E7EB)',
    color: 'var(--gf-secondary-500, #4B5563)', fontSize: '14px', lineHeight: 1,
  },
};

export function FilterSection({ label, meta, action, onAction, first, children }) {
  // Inside a FilterGroup the group header already carries the name, so a
  // section with nothing left to say drops its head row entirely rather than
  // rendering an empty one and paying for the border and spacing.
  const hasHead = label || meta || action;
  return (
    <div style={{ ...t.section, ...(first || !hasHead ? t.sectionFirst : {}) }}>
      {hasHead ? (
        <div style={t.head}>
          <span style={t.label}>{label}</span>
          {meta ? <span style={t.meta}>{meta}</span> : null}
          {action ? <button type="button" style={t.action} onClick={onAction}>{action}</button> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

/** Toggleable chip. `mono` for numeric values so digits line up. */
export function Chip({ children, selected, mono, onClick, title }) {
  return (
    <button
      type="button"
      title={title}
      aria-pressed={!!selected}
      onClick={onClick}
      style={{ ...t.chip, ...(mono ? t.mono : {}), ...(selected ? t.chipOn : {}) }}
    >
      {children}
    </button>
  );
}

export function ChipRow({ children }) {
  return <div style={t.chips}>{children}</div>;
}

/**
 * Quick-value chips over a threshold. Selecting one applies immediately, which
 * is the whole point - the common case (bump the gold cut-off) becomes one
 * click instead of select-all-and-retype.
 */
export function PresetChips({ values, value, unit, onChange }) {
  return (
    <ChipRow>
      {values.map((v) => (
        <Chip key={v} mono selected={Number(value) === v} onClick={() => onChange(v)}
              title={unit ? `${v} ${unit}` : String(v)}>
          {v}
        </Chip>
      ))}
    </ChipRow>
  );
}

/**
 * Numeric field with -/+ steppers. Fires onChange on every edit so the layer
 * refilters as you type; there is no commit step.
 */
export function Stepper({ value, unit, step = 1, min = 0, max, onChange }) {
  const num = Number(value) || 0;
  const clamp = (n) => {
    let v = n;
    if (min !== undefined && v < min) v = min;
    if (max !== undefined && v > max) v = max;
    return Number(v.toFixed(4));
  };
  return (
    <div style={t.stepbox}>
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => onChange(e.target.value === '' ? '' : clamp(Number(e.target.value)))}
        style={t.stepInput}
        aria-label={unit ? `Value in ${unit}` : 'Value'}
      />
      {unit ? <span style={t.unit}>{unit}</span> : null}
      <button type="button" style={t.pm} onClick={() => onChange(clamp(num - step))} aria-label="Decrease">−</button>
      <button type="button" style={t.pm} onClick={() => onChange(clamp(num + step))} aria-label="Increase">+</button>
    </div>
  );
}

/**
 * Multi-select as chips. Shows what is on without opening anything, and the
 * counter tells you how much of the set you have hidden.
 */
export function ChipMultiSelect({ options, selected, onChange, label, meta, allowEmpty = true }) {
  const isOn = (v) => selected.includes(v);
  const toggle = (v) => {
    const next = isOn(v) ? selected.filter((s) => s !== v) : [...selected, v];
    if (!allowEmpty && next.length === 0) return;
    onChange(next);
  };
  return (
    <FilterSection label={label} meta={meta === null ? undefined : (meta ?? `${selected.length} of ${options.length}`)}>
      <ChipRow>
        {options.map((o) => {
          const val = typeof o === 'string' ? o : o.value;
          const text = typeof o === 'string' ? o : o.label;
          return <Chip key={val} selected={isOn(val)} onClick={() => toggle(val)}>{text}</Chip>;
        })}
      </ChipRow>
    </FilterSection>
  );
}

/**
 * Opacity slider for raster overlays.
 *
 * Callers gate this on the group actually having a visible layer - an opacity
 * control with nothing to apply it to is just a dead widget, and we shipped two
 * of them. The percentage is mirrored into --gf-range-fill so the CSS can paint
 * the filled part of the track; see .gf-range in styles/map-controls.css.
 */
export function OpacitySlider({ value, onChange, min = 0, max = 1, step = 0.05, label = 'Opacity' }) {
  const v = typeof value === 'number' ? value : max;
  const pct = Math.round(((v - min) / (max - min)) * 100);
  return (
    <div style={{ padding: 'var(--gf-space-2, 8px) 4px 10px' }}>
      <div style={{ ...t.head, marginBottom: 7 }}>
        <span style={t.label}>{label}</span>
        <span style={{ ...t.meta, ...t.mono }}>{Math.round(v * 100)}%</span>
      </div>
      <input
        className="gf-range"
        type="range"
        min={min}
        max={max}
        step={step}
        value={v}
        aria-label={`${label} percent`}
        style={{ '--gf-range-fill': `${pct}%` }}
        onChange={(e) => onChange(parseFloat(e.target.value))}
      />
    </div>
  );
}

/**
 * A collapsible group of filter sections.
 *
 * Collapsing the Simple/Advanced tabs into one panel put every control on one
 * scroll, which is right - tabs hid the upper bound, the site-stage toggles and
 * "Expiring soon" behind clicks nobody made. But for tenements that produced a
 * panel long enough to be unusable.
 *
 * Groups are the middle ground: everything is still on one scroll and nothing
 * is behind a tab, but only what you reach for constantly is open. `summary`
 * matters - a collapsed group that says "3 of 9" or "Live, Pending" still tells
 * you what it is doing, so you do not have to open it to find out.
 */
export function FilterGroup({ label, summary, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={t.group}>
      <button
        type="button"
        style={t.groupHead}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span style={t.label}>{label}</span>
        <span style={t.groupRight}>
          {summary ? <span style={t.meta}>{summary}</span> : null}
          <span style={{ ...t.chevron, transform: open ? 'rotate(180deg)' : 'none' }}>⌄</span>
        </span>
      </button>
      {open ? <div style={t.groupBody}>{children}</div> : null}
    </div>
  );
}

export const filterStyles = t;
